import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/css/admin/site/SiteAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";

import flowerIcon from "../../../assets/img/flower_icon.png";

import {
  getSiteContact,
  saveSiteContact,
  getFaqList,
  saveFaqList,
  getMainBanners,
  saveMainBanners,
} from "../../../api/siteApi";

import { getFileUrl } from "../../../api/apiClient";

const isVideoUrl = (url = "") => {
  const cleanUrl = String(url || "").split("?")[0].toLowerCase();

  return (
    cleanUrl.endsWith(".mp4") ||
    cleanUrl.endsWith(".mov") ||
    cleanUrl.endsWith(".webm") ||
    cleanUrl.endsWith(".ogg")
  );
};

const isVideoFile = (file) => {
  return file?.type?.startsWith("video/");
};

const formatBanners = (result) => {
  return (result || []).map((banner, index) => {
    const mediaUrl =
      banner.mediaUrl ||
      banner.url ||
      banner.fileUrl ||
      banner.imageUrl ||
      banner.videoUrl ||
      banner.savedUrl ||
      "";

    const previewUrl = getFileUrl(mediaUrl);

    return {
      id:
        banner.mainBannerId ||
        banner.bannerId ||
        banner.id ||
        Date.now() + index,
      mainBannerId: banner.mainBannerId || banner.bannerId || banner.id || null,
      file: null,
      preview: previewUrl,
      type: isVideoUrl(mediaUrl) ? "video" : "image",
      isExisting: true,
    };
  });
};

function SiteAdmin() {
  const navigate = useNavigate();
  const params = useParams();

  const activeMenu = params.menu || "contact";

  const [contactNotice, setContactNotice] = useState("");
  const [contactLinks, setContactLinks] = useState({
    homepage: "",
    email: "",
    cooperationEmail: "",
    kakao: "",
    instagram: "",
    youtube: "",
  });

  const [faqList, setFaqList] = useState([
    {
      id: Date.now(),
      faqId: null,
      question: "",
      answer: "",
    },
  ]);

  const [banners, setBanners] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const pageTitle = useMemo(() => {
    if (activeMenu === "faq") return "FAQ 관리";
    if (activeMenu === "banner") return "메인 배너 관리";
    return "소통 관리";
  }, [activeMenu]);

  const handleChangeMenu = (menu) => {
    navigate(`/stdcounciladmin/adminhome/site/${menu}`);
  };

  useEffect(() => {
    const fetchSiteContact = async () => {
      if (activeMenu !== "contact") return;

      try {
        setIsLoading(true);

        const result = await getSiteContact();

        console.log("소통 관리 조회 성공:", result);

        setContactNotice(result?.contactInfo || "");

        setContactLinks({
          homepage: result?.homepageUrl || "",
          email: result?.email || "",
          cooperationEmail: result?.partnerEmail || "",
          kakao: result?.kakaoUrl || "",
          instagram: result?.instagramUrl || "",
          youtube: result?.youtubeUrl || "",
        });
      } catch (error) {
        console.error("소통 관리 조회 실패:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSiteContact();
  }, [activeMenu]);

  useEffect(() => {
    const fetchFaqList = async () => {
      if (activeMenu !== "faq") return;

      try {
        setIsLoading(true);

        const result = await getFaqList();

        console.log("FAQ 조회 성공:", result);

        const formattedFaqs = (result || []).map((faq, index) => ({
          id: faq.faqId || faq.id || Date.now() + index,
          faqId: faq.faqId || faq.id || null,
          question: faq.question || "",
          answer: faq.answer || "",
        }));

        setFaqList(
          formattedFaqs.length > 0
            ? formattedFaqs
            : [
                {
                  id: Date.now(),
                  faqId: null,
                  question: "",
                  answer: "",
                },
              ]
        );
      } catch (error) {
        console.error("FAQ 조회 실패:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFaqList();
  }, [activeMenu]);

  useEffect(() => {
    const fetchBanners = async () => {
      if (activeMenu !== "banner") return;

      try {
        setIsLoading(true);

        const result = await getMainBanners();

        console.log("메인 배너 조회 성공:", result);

        const formattedBanners = formatBanners(result);

        setBanners(formattedBanners);
      } catch (error) {
        console.error("메인 배너 조회 실패:", error);
        setBanners([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanners();
  }, [activeMenu]);

  const handleContactLinkChange = (key, value) => {
    setContactLinks((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleContactSave = async () => {
    try {
      setIsSaving(true);

      const requestData = {
        contactInfo: contactNotice,
        homepageUrl: contactLinks.homepage,
        email: contactLinks.email,
        partnerEmail: contactLinks.cooperationEmail,
        kakaoUrl: contactLinks.kakao,
        instagramUrl: contactLinks.instagram,
        youtubeUrl: contactLinks.youtube,
      };

      console.log("소통 관리 저장 요청:", requestData);

      await saveSiteContact(requestData);

      alert("소통 관리가 저장되었습니다.");
    } catch (error) {
      console.error("소통 관리 저장 실패:", error);
      alert(error.message || "소통 관리 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddFaq = () => {
    setFaqList((prev) => [
      ...prev,
      {
        id: Date.now(),
        faqId: null,
        question: "",
        answer: "",
      },
    ]);
  };

  const handleFaqChange = (id, key, value) => {
    setFaqList((prev) =>
      prev.map((faq) =>
        faq.id === id
          ? {
              ...faq,
              [key]: value,
            }
          : faq
      )
    );
  };

  const handleDeleteFaq = (id) => {
    const confirmDelete = window.confirm("삭제하시겠습니까?");
    if (!confirmDelete) return;

    setFaqList((prev) => prev.filter((faq) => faq.id !== id));
  };

  const handleFaqSave = async () => {
    const invalidFaq = faqList.some(
      (faq) => !faq.question.trim() || !faq.answer.trim()
    );

    if (invalidFaq) {
      alert("질문과 답변을 모두 입력해 주세요.");
      return;
    }

    try {
      setIsSaving(true);

      const requestFaqs = faqList.map((faq) => ({
        faqId: faq.faqId || null,
        question: faq.question,
        answer: faq.answer,
      }));

      console.log("FAQ 저장 요청:", { faqs: requestFaqs });

      await saveFaqList(requestFaqs);

      alert("FAQ가 저장되었습니다.");

      const result = await getFaqList();

      const formattedFaqs = (result || []).map((faq, index) => ({
        id: faq.faqId || faq.id || Date.now() + index,
        faqId: faq.faqId || faq.id || null,
        question: faq.question || "",
        answer: faq.answer || "",
      }));

      setFaqList(
        formattedFaqs.length > 0
          ? formattedFaqs
          : [
              {
                id: Date.now(),
                faqId: null,
                question: "",
                answer: "",
              },
            ]
      );
    } catch (error) {
      console.error("FAQ 저장 실패:", error);
      alert(error.message || "FAQ 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBannerChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) return;

    const newBanners = selectedFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      mainBannerId: null,
      file,
      preview: URL.createObjectURL(file),
      type: isVideoFile(file) ? "video" : "image",
      isExisting: false,
    }));

    setBanners((prev) => [...prev, ...newBanners]);

    e.target.value = "";
  };

  const handleDeleteBanner = (id) => {
    setBanners((prev) => {
      const target = prev.find((banner) => banner.id === id);

      if (target?.preview && target.file) {
        URL.revokeObjectURL(target.preview);
      }

      return prev.filter((banner) => banner.id !== id);
    });
  };

  const handleBannerSave = async () => {
    try {
      setIsSaving(true);

      const files = banners.map((banner) => banner.file).filter(Boolean);

      const keepBannerIds = banners
        .filter((banner) => !banner.file && banner.mainBannerId)
        .map((banner) => banner.mainBannerId);

      console.log("유지할 기존 배너 ID:", keepBannerIds);
      console.log("새로 추가할 배너 파일:", files);

      await saveMainBanners(files, keepBannerIds);

      alert("메인 배너가 저장되었습니다.");

      const result = await getMainBanners();

      console.log("메인 배너 저장 후 재조회:", result);

      const formattedBanners = formatBanners(result);

      setBanners(formattedBanners);
    } catch (error) {
      console.error("메인 배너 저장 실패:", error);
      alert(error.message || "메인 배너 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderBannerPreview = (banner) => {
    if (!banner.preview) {
      return <span className="site-banner-empty">배너</span>;
    }

    if (banner.type === "video") {
      return (
        <video
          src={banner.preview}
          className="site-banner-preview"
          muted
          controls
          playsInline
        />
      );
    }

    return (
      <img
        src={banner.preview}
        alt="배너 미리보기"
        className="site-banner-preview"
      />
    );
  };

  return (
    <div className="site-admin-page admin-scroll-x">
      <div className="site-admin-inner">
        <h2 className="site-admin-title">{pageTitle}</h2>

        <div className="site-admin-tabs">
          <button
            type="button"
            className={activeMenu === "contact" ? "active" : ""}
            onClick={() => handleChangeMenu("contact")}
          >
            소통 관리
          </button>

          <button
            type="button"
            className={activeMenu === "faq" ? "active" : ""}
            onClick={() => handleChangeMenu("faq")}
          >
            FAQ 관리
          </button>

          <button
            type="button"
            className={activeMenu === "banner" ? "active" : ""}
            onClick={() => handleChangeMenu("banner")}
          >
            메인 배너 관리
          </button>
        </div>

        {isLoading && <div className="site-loading">불러오는 중입니다.</div>}

        {activeMenu === "contact" && (
          <section className="site-section">
            <div className="site-field-block">
              <div className="site-label-row">
                <img src={flowerIcon} alt="" className="site-flower-icon" />
                <span className="site-label">소통 안내</span>
              </div>

              <textarea
                className="site-notice-textarea"
                placeholder="문의 관련 안내 사항을 작성하세요."
                value={contactNotice}
                onChange={(e) => setContactNotice(e.target.value)}
              />
            </div>

            <div className="site-divider" />

            <h3 className="site-sub-title">소통 링크</h3>

            <div className="site-link-list">
              <SiteLinkInput
                label="홈페이지"
                value={contactLinks.homepage}
                onChange={(value) => handleContactLinkChange("homepage", value)}
              />

              <SiteLinkInput
                label="이메일"
                value={contactLinks.email}
                onChange={(value) => handleContactLinkChange("email", value)}
              />

              <SiteLinkInput
                label="협력 이메일"
                value={contactLinks.cooperationEmail}
                onChange={(value) =>
                  handleContactLinkChange("cooperationEmail", value)
                }
              />

              <SiteLinkInput
                label="카카오톡"
                value={contactLinks.kakao}
                onChange={(value) => handleContactLinkChange("kakao", value)}
              />

              <SiteLinkInput
                label="인스타그램"
                value={contactLinks.instagram}
                onChange={(value) =>
                  handleContactLinkChange("instagram", value)
                }
              />

              <SiteLinkInput
                label="유튜브"
                value={contactLinks.youtube}
                onChange={(value) => handleContactLinkChange("youtube", value)}
              />
            </div>

            <div className="site-save-row">
              <button
                type="button"
                className="site-save-btn"
                onClick={handleContactSave}
                disabled={isSaving}
              >
                {isSaving ? "저장 중..." : "저장"}
              </button>
            </div>
          </section>
        )}

        {activeMenu === "faq" && (
          <section className="site-section">
            <button
              type="button"
              className="site-add-btn"
              onClick={handleAddFaq}
              disabled={isSaving}
            >
              +
            </button>

            <div className="site-faq-list">
              {faqList.map((faq) => (
                <div className="site-faq-item" key={faq.id}>
                  <div className="site-form-row">
                    <div className="site-label-row site-small-label">
                      <img
                        src={flowerIcon}
                        alt=""
                        className="site-flower-icon"
                      />
                      <span className="site-label">질문</span>
                    </div>

                    <input
                      className="site-input"
                      type="text"
                      placeholder="질문을 입력해 주세요."
                      value={faq.question}
                      onChange={(e) =>
                        handleFaqChange(faq.id, "question", e.target.value)
                      }
                    />
                  </div>

                  <div className="site-form-row site-form-row-top">
                    <div className="site-label-row site-small-label">
                      <img
                        src={flowerIcon}
                        alt=""
                        className="site-flower-icon"
                      />
                      <span className="site-label">답변</span>
                    </div>

                    <textarea
                      className="site-answer-textarea"
                      placeholder="문의 답변을 입력해 주세요."
                      value={faq.answer}
                      onChange={(e) =>
                        handleFaqChange(faq.id, "answer", e.target.value)
                      }
                    />
                  </div>

                  <div className="site-delete-row">
                    <button
                      type="button"
                      className="site-delete-btn"
                      onClick={() => handleDeleteFaq(faq.id)}
                      disabled={isSaving}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="site-save-row">
              <button
                type="button"
                className="site-save-btn"
                onClick={handleFaqSave}
                disabled={isSaving}
              >
                {isSaving ? "저장 중..." : "저장"}
              </button>
            </div>
          </section>
        )}

        {activeMenu === "banner" && (
          <section className="site-section">
            <div className="site-field-block">
              <div className="site-label-row">
                <img src={flowerIcon} alt="" className="site-flower-icon" />
                <span className="site-label">이미지 및 동영상</span>
              </div>

              <div className="site-banner-box">
                {banners.map((banner) => (
                  <div className="site-banner-card" key={banner.id}>
                    <button
                      type="button"
                      className="site-banner-remove"
                      onClick={() => handleDeleteBanner(banner.id)}
                      disabled={isSaving}
                    >
                      ×
                    </button>

                    {renderBannerPreview(banner)}
                  </div>
                ))}

                <label className="site-banner-add">
                  +
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,video/*"
                    onChange={handleBannerChange}
                  />
                </label>
              </div>
            </div>

            <div className="site-save-row">
              <button
                type="button"
                className="site-save-btn"
                onClick={handleBannerSave}
                disabled={isSaving}
              >
                {isSaving ? "저장 중..." : "저장"}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function SiteLinkInput({ label, value, onChange }) {
  return (
    <div className="site-link-row">
      <div className="site-label-row site-link-label">
        <img src={flowerIcon} alt="" className="site-flower-icon" />
        <span className="site-label">{label}</span>
      </div>

      <input
        className="site-input"
        type="text"
        placeholder="링크를 입력해 주세요."
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export default SiteAdmin;